import {
  type ContentType,
  type StoryblokRichTextDoc,
  type StoryblokRichTextType,
} from '../../storyblok'
import { walkStoryblokBlocks } from '../../walkStoryblokBlocks'

type RichTextNode = StoryblokRichTextDoc['content'][number]
type HeadingNode = Extract<RichTextNode, { type: 'heading' }>

const getAnchor = (content: RichTextNode): string | undefined => {
  if (!('marks' in content) || !content.marks) {
    return undefined
  }

  for (const mark of content.marks) {
    if (
      mark.type === 'anchor' &&
      typeof mark.attrs.id === 'string' &&
      mark.attrs.id
    ) {
      return mark.attrs.id
    }
  }

  return undefined
}

const TOC_HEADING_LEVEL = 2

type TocItem = {
  anchor: string
  text: string
}

const getRichTextHeadings = (
  block: StoryblokRichTextType,
): TocItem[] | undefined => {
  const tocItems: TocItem[] = []

  const headings = block.content.content.filter(
    (
      content,
    ): content is HeadingNode & {
      content: RichTextNode[]
    } =>
      content.type === 'heading' &&
      content.attrs.level === TOC_HEADING_LEVEL &&
      Boolean(content.content?.length),
  )

  for (const heading of headings) {
    let anchor: string | undefined
    let text = ''

    for (const content of heading.content) {
      anchor ??= getAnchor(content)

      if ('text' in content && content.text) {
        text += content.text
      }
    }

    if (anchor && text) {
      tocItems.push({
        anchor,
        text,
      })
    }
  }

  return tocItems.length === 0 ? undefined : tocItems
}

/**
 * Extract Table of Contents headings level 2 from `HeadingV2` and `RichText` components
 */
export const cmsGetTocItems = (content: ContentType): TocItem[] => {
  const headings: TocItem[] = []

  walkStoryblokBlocks(content, (block) => {
    if (
      block.component === 'HeadingV2' &&
      block.level === String(TOC_HEADING_LEVEL) &&
      block.id &&
      block.text
    ) {
      headings.push({
        anchor: block.id,
        text: block.text,
      })
    }

    if (block.component !== 'RichText') {
      return
    }

    const richTextHeadings = getRichTextHeadings(block)

    if (richTextHeadings) {
      headings.push(...richTextHeadings)
    }
  })

  return headings
}
